import { Link } from '@tanstack/react-router';

import type { GapDraftView } from '@momentum/types';

import { useGapExecution } from '../../../../shared/hooks/useGapReview';
import { duration, shortDate, tokenCount } from '../../lib/format';
import { totalsOf, verdictTone } from '../../lib/run-audit';
import { SCORE_LABEL, SCORE_ORDER } from '../../lib/scores';

function ExecutionSummary({ draft }: { draft: GapDraftView }) {
  const {
    data: execution,
    isPending,
    error,
  } = useGapExecution(draft.executionId);

  if (isPending) {
    return (
      <div>
        <h3 className="gr-insp-h">Execution summary</h3>
        <div className="gr-empty">Loading the execution…</div>
      </div>
    );
  }

  if (error !== null) {
    return (
      <div>
        <h3 className="gr-insp-h">Execution summary</h3>
        <div className="gr-empty">
          <b>Could not load the execution</b>
          {error.message}
        </div>
      </div>
    );
  }

  const totals = totalsOf(execution);

  return (
    <>
      <div>
        <h3 className="gr-insp-h">Execution summary</h3>
        <div className="gr-runmini">
          <div>
            <span>Completed</span>
            <b>{shortDate(execution.completedAt)}</b>
          </div>
          <div>
            <span>Data cutoff</span>
            <b>{shortDate(execution.dataCutoff)}</b>
          </div>
          <div>
            <span>Iterations</span>
            <b>{execution.runsExecuted}</b>
          </div>
          <div>
            <span>Gaps</span>
            <b>{totals.gapCount}</b>
          </div>
          <div>
            <span>Candidates</span>
            <b>{totals.candidateCount}</b>
          </div>
          <div>
            <span>Missed sources</span>
            <b>{totals.missedSourceCount}</b>
          </div>
        </div>
        <div className="gr-fact gr-stack-top">
          <span>stop reason</span>
          <span className="gr-fact-v mono" data-tone="amber">
            {execution.stopReason}
          </span>
        </div>
      </div>

      {/* Absent on executions that predate the telemetry, and nothing
          backfilled them — so the whole block is omitted rather than shown
          empty. Six columns is what 390px holds: role over model, then the
          three numbers that say whether an agent was starved of context or
          died mid-way. The full labels and token totals are in the audit. */}
      {!!execution.agents?.length && (
        <div>
          <h3 className="gr-insp-h">
            Agents · {execution.agents.length} invocation
            {execution.agents.length === 1 ? '' : 's'}
          </h3>
          <div className="gr-agentgrid gr-agentgrid-mini">
            <div className="gr-agentgrid-row gr-agentgrid-row-head">
              <span>agent</span>
              <span>turns</span>
              <span>peak ctx</span>
              <span>cmpct</span>
              <span>time</span>
            </div>
            {execution.agents.map((agent, index) => (
              // Labels repeat when the same agent runs over several batches, so
              // the index is what actually separates two invocations.
              <div className="gr-agentgrid-row" key={`${agent.label}-${index}`}>
                <span>
                  <b>{agent.role}</b>
                  <i className="gr-agentgrid-role mono">{agent.model}</i>
                </span>
                <span>{agent.turns ?? '—'}</span>
                <span>{tokenCount(agent.peakContextTokens)}</span>
                <span>{agent.compactions ?? '—'}</span>
                <span>{duration(agent.durationMs)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <h3 className="gr-insp-h">Verdicts in this execution</h3>
        <div className="gr-verdictpills">
          {totals.verdictCounts.map(({ verdict, count }) => (
            <span
              className="gr-vpill"
              data-tone={verdictTone(verdict)}
              key={verdict}
            >
              {count} {verdict.toLowerCase()}
            </span>
          ))}
        </div>
      </div>
    </>
  );
}

/**
 * Run tab — the execution summary and the draft's provenance.
 *
 * The rail carries the summary only; the full audit is its own view, because the
 * iterations, coverage notes and candidate dispositions do not fit 390px and are
 * not what a reviewer needs mid-queue.
 *
 * The summary needs fields `GET /gap-review/drafts` does not carry, so it fetches
 * the execution — which only happens when this tab is open, and warms the cache
 * the audit view then reads. Provenance comes off the draft and renders either
 * way, so a failed fetch costs the reviewer the tallies, not the tab.
 */
export function RunTab({ draft }: { draft: GapDraftView }) {
  const { source } = draft;

  return (
    <>
      <ExecutionSummary draft={draft} />

      <div>
        <h3 className="gr-insp-h">Finder scores</h3>
        <div className="gr-provenance">
          {SCORE_ORDER.map((axis) => (
            <div className="gr-prov" key={axis}>
              <span>{SCORE_LABEL[axis]}</span>
              <span>{source[axis]}</span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="gr-insp-h">Provenance</h3>
        <div className="gr-provenance">
          <div className="gr-prov">
            <span>detector</span>
            <span>{source.metadata.detector}</span>
          </div>
          <div className="gr-prov">
            <span>claimType</span>
            <span>{source.claimType}</span>
          </div>
          <div className="gr-prov">
            <span>foundBy</span>
            <span>{source.metadata.foundBy}</span>
          </div>
          <div className="gr-prov">
            <span>runNumber</span>
            <span>{source.metadata.runNumber}</span>
          </div>
          <div className="gr-prov">
            <span>metadata.id</span>
            <span>{source.metadata.id}</span>
          </div>
          <div className="gr-prov">
            <span>sourceCandidateIds</span>
            <span>{source.metadata.sourceCandidateIds.join(', ')}</span>
          </div>
          <div className="gr-prov">
            <span>isHypothesis</span>
            <span>{String(source.isHypothesis ?? false)}</span>
          </div>
        </div>
      </div>

      {/* Outside `ExecutionSummary` on purpose: the audit needs only the id the
          draft already carries, so a failed summary fetch must not take the way
          into the audit with it. */}
      <Link
        className="gr-btn gr-btn-block"
        to="/gap-review/run/$executionId"
        params={{ executionId: draft.executionId }}
      >
        Open the full run audit
      </Link>
    </>
  );
}
